import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { ReferenceCardModelView } from '@/shared/model/reference.model';
import { REFERENCE_STYLE_FOR, REFERENCE_TYPE } from '@/constants';
import { getFileTypeAndImageByDocument, getHostName } from '@/util/ds-util';
import { checkIsFileByDocument, checkIsNoteByDocument, getFaviconByUrl } from '@/util/common-util';
import { hideElementById } from '@/util/style-util';
import { formatDate } from '../../../util/date-util';

const briefingStore = namespace('briefingStore');
const appStoreState = namespace('appStore');

@Component({
  methods: { formatDate, hideElementById },
  computed: {
    REFERENCE_STYLE_FOR() {
      return REFERENCE_STYLE_FOR;
    },
    REFERENCE_TYPE() {
      return REFERENCE_TYPE;
    },
  },
})
export default class ReferenceCardComponent extends Vue {
  @Prop(Object) readonly formModel?: any;
  @Prop(String) readonly styleFor?: string;
  @Prop(Array) readonly checkedReferenceIds?: Array<string>;
  @Prop(Boolean) readonly isWaitingResponse?: boolean;
  @Prop(Boolean) readonly isLoadingSaveBriefingResource?: boolean;
  @Prop(Number) readonly indexItem?: number;

  @briefingStore.Getter
  getAddingReferenceDocuments!: Array<any>;

  @appStoreState.Getter
  getErrorElementId!: Array<string>;

  @appStoreState.Mutation
  setErrorElementId!: (payload: string[]) => void;

  dataModelForView: ReferenceCardModelView = {
    id: '',
    title: '',
    channel: '',
    description: '',
    fileSize: '',
    type: '',
    isNew: false,
    indexNumber: '',
    originUrl: '',
    fileType: '',
    sourceUriDomain: '',
    link: '',
    writer: '',
    search_type: '',
    faviconImage: '',
    path: '',
    imageUriThumbnail: '',
    published_at: '',
  };

  mounted() {
    this.mapData();
  }

  @Watch('formModel')
  onFormModelChanged() {
    this.mapData();
  }

  makeData() {
    this.dataModelForView.id =
      this.formModel?.docId ||
      this.formModel?.parentDocId ||
      this.formModel?.originParentDocDetail?.parent_doc_id?.[0] ||
      this.formModel?.parent_doc_id ||
      this.formModel?.doc_id ||
      this.formModel?._id ||
      this.formModel?.id ||
      '';
    this.dataModelForView.title = this.formModel?.title || this.formModel?.originParentDocDetail?.title || '';
    this.dataModelForView.channel = this.getChanel(this.formModel) || '';
    this.dataModelForView.description = this.formModel?.description || this.formModel?.originParentDocDetail?.source_summary || '';
    this.dataModelForView.fileSize = this.formModel?.fileSize || '';
    this.dataModelForView.type = this.formModel?.type || '';
    this.dataModelForView.isNew = this.formModel?.isNew || false;
    this.dataModelForView.indexNumber =
      this.formModel?.referenceDocumentIndex || (this.indexItem !== undefined ? this.indexItem + 1 : '') || '';
    const tempUrl = this.formModel?.path || this.formModel?.originParentDocDetail?.source_uri || this.formModel?.source_uri || '';
    this.dataModelForView.originUrl = tempUrl;
    this.dataModelForView.link = tempUrl;
    this.dataModelForView.path = tempUrl;
    this.dataModelForView.fileType = this.formModel.fileType || '';
    this.dataModelForView.sourceUriDomain = this.formModel?.originParentDocDetail?.source_uri_domain || '';
    this.dataModelForView.writer = this.formModel?.writer?.[0] || '';
    this.dataModelForView.search_type = this.formModel?.search_type || this.formModel?.originParentDocDetail?.search_type || '';
    this.dataModelForView.imageUriThumbnail = this.formModel?.originParentDocDetail?.image_uri_thumbnail || '';
    this.dataModelForView.faviconImage =
      getFaviconByUrl(this.formModel?.path || this.formModel?.originParentDocDetail?.source_uri || '') || '';

    if (checkIsFileByDocument(this.dataModelForView)) {
      this.dataModelForView.type = REFERENCE_TYPE.FILE.toString();
      const fileObject = getFileTypeAndImageByDocument(this.dataModelForView) || undefined;

      if (fileObject !== undefined) {
        this.dataModelForView.imageUriThumbnail = fileObject.fileCardImage || '';
      }
    }

    if (checkIsNoteByDocument(this.dataModelForView)) {
      this.dataModelForView.type = REFERENCE_TYPE.NOTE.toString();
    }
  }

  mapData() {
    // console.log('this.formModel: ', this.formModel);
    this.dataModelForView = { ...this.formModel };

    if (this.styleFor !== undefined) {
      if (
        this.styleFor === REFERENCE_STYLE_FOR.BRIEFING_REFERENCE ||
        (this.styleFor === REFERENCE_STYLE_FOR.EDIT_REFERENCE.toString() && (this.formModel?.data === undefined || !this.formModel?.data))
      ) {
        this.makeData();
      } else if (
        (this.styleFor === REFERENCE_STYLE_FOR.EDIT_REFERENCE.toString() ||
          this.styleFor === REFERENCE_STYLE_FOR.REPORT_REFERENCE.toString()) &&
        this.formModel?.data !== undefined
      ) {
        this.dataModelForView.id =
          this.formModel?.id ||
          this.formModel?.data?._id ||
          this.formModel?.docId ||
          this.formModel?.parentDocId ||
          this.formModel?.originParentDocDetail?.parent_doc_id?.[0] ||
          this.formModel?.parent_doc_id ||
          this.formModel?.doc_id ||
          this.formModel?._id ||
          '';
        this.dataModelForView.title = this.formModel?.data?.title || this.formModel?.title || '';
        this.dataModelForView.channel =
          this.formModel?.data?.source_chanel || this.formModel?.data?.channel || this.getChanel(this.formModel.data) || '';
        this.dataModelForView.description =
          this.formModel?.data?.content ||
          this.formModel?.data?.description ||
          this.formModel?.data?.source_summary ||
          this.formModel?.content ||
          this.formModel?.description ||
          '';
        this.dataModelForView.isNew = this.formModel?.isNew || this.formModel?.data?.isNew || false;
        this.dataModelForView.imageUriThumbnail =
          this.formModel?.data?.image_uri_thumbnail ||
          this.formModel?.image_uri_thumbnail ||
          this.formModel?.originParentDocDetail?.image_uri_thumbnail ||
          '';

        this.dataModelForView.indexNumber =
          this.formModel?.referenceDocumentIndex || (this.indexItem !== undefined ? this.indexItem + 1 : '') || '';

        const tempUrl =
          this.formModel?.data?.path ||
          this.formModel?.data?.source_uri ||
          this.formModel?.source_uri ||
          this.formModel?.path ||
          this.formModel?.originParentDocDetail?.source_uri ||
          this.formModel?.data?.originParentDocDetail?.source_uri ||
          '';

        this.dataModelForView.originUrl = tempUrl;
        this.dataModelForView.link = tempUrl;
        this.dataModelForView.path = tempUrl;
        this.dataModelForView.fileType = this.formModel?.fileType || '';
        this.dataModelForView.sourceUriDomain =
          this.formModel?.data?.source_uri_domain || this.formModel?.originParentDocDetail?.source_uri_domain || '';
        this.dataModelForView.writer = this.formModel?.data?.writer?.[0] || this.formModel?.writer?.[0] || '';
        this.dataModelForView.search_type =
          this.formModel?.data?.search_type || this.formModel?.search_type || this.formModel?.originParentDocDetail?.search_type || '';
        this.dataModelForView.fileSize = this.formModel?.fileSize || '';
        this.dataModelForView.type = this.formModel?.type || '';
        this.dataModelForView.published_at = this.formModel?.data?.published_at || this.formModel?.data?.published_at_string || '';
        this.dataModelForView.faviconImage =
          getFaviconByUrl(
            this.formModel?.data?.source_uri || this.formModel?.path || this.formModel?.originParentDocDetail?.source_uri || ''
          ) || '';
        if (checkIsFileByDocument(this.dataModelForView)) {
          this.dataModelForView.type = REFERENCE_TYPE.FILE.toString();

          const fileObject = getFileTypeAndImageByDocument(this.dataModelForView) || undefined;

          if (fileObject !== undefined) {
            this.dataModelForView.imageUriThumbnail = fileObject.fileCardImage || '';
          }
        }
        if (checkIsNoteByDocument(this.dataModelForView)) {
          this.dataModelForView.type = REFERENCE_TYPE.NOTE.toString();
        }
      }
    }
  }

  getClassFor() {
    if (
      this.styleFor !== undefined &&
      this.styleFor === REFERENCE_STYLE_FOR.EDIT_REFERENCE.toString() &&
      this.dataModelForView.imageUriThumbnail
    ) {
      return 'max-width-186px';
    } else {
      const hasImage = document.getElementsByClassName('image-view-element');
      return hasImage ? 'width-100' : 'width-82';
    }
  }

  showDetail(document: any) {
    if (this.styleFor !== undefined && this.styleFor === REFERENCE_STYLE_FOR.BRIEFING_REFERENCE.toString()) {
      this.$emit('showDetail', document);
    }
    // } else if (this.styleFor !== undefined && this.styleFor === REFERENCE_STYLE_FOR.REPORT_REFERENCE.toString()) {
    //   const originUrl = document?.data?.source_uri || this.dataModelForView?.originUrl || '';
    //
    //   if (originUrl?.trim()?.length > 0) {
    //     this.openOriginal(originUrl);
    //   }
    // }
  }

  handleClickCheckedReference(document: any) {
    this.$emit('handleClickCheckedReference', document);
  }

  handleDeleteReference(indexInput: number) {
    this.$emit('handleDeleteReference', indexInput);
  }

  getFullChanel(documentItem: ReferenceCardModelView): string {
    let tempChannel = '';

    if (documentItem) {
      if (documentItem.sourceUriDomain) {
        tempChannel = documentItem.sourceUriDomain;
      } else {
        tempChannel = this.getHostname(documentItem.originUrl);
      }
    }

    return tempChannel;
  }

  getHostname(url) {
    try {
      const parsedUrl = new URL(url);

      return parsedUrl.hostname;
    } catch (e) {
      return url;
    }
  }

  getChanel(documentItem: any): string {
    // console.log('documentItem: ', documentItem);

    let tempChannel = '';

    if (documentItem) {
      tempChannel = documentItem.channel
        ? documentItem.channel
        : documentItem.link
        ? getHostName(documentItem.link)
        : documentItem.url
        ? getHostName(documentItem.url)
        : documentItem.path
        ? getHostName(documentItem.path)
        : documentItem.source_uri
        ? getHostName(documentItem.source_uri)
        : documentItem.sourceUriDomain
        ? getHostName(documentItem.sourceUriDomain)
        : documentItem.source_uri_domain
        ? getHostName(documentItem.source_uri_domain)
        : documentItem.writer
        ? getHostName(documentItem.writer)
        : '';

      if (
        (documentItem.search_type && documentItem.search_type.length > 0 && documentItem.search_type[0] === 'USERNOTE') ||
        checkIsNoteByDocument(documentItem)
      ) {
        tempChannel = 'Note';
      }

      if (checkIsFileByDocument(documentItem)) {
        tempChannel = 'File';
      }

      if (tempChannel === null || tempChannel === undefined) {
        tempChannel = '';
      }
    }

    return tempChannel;
  }

  hasntTopic(docId: string): boolean {
    return docId && !docId.includes('topic');
  }

  openOriginal(url) {
    window.open(url, '_blank');
  }

  hideElementByIdLocalComponent(idInput: string) {
    if (idInput?.trim()?.length > 0) {
      const tempList = this.getErrorElementId?.length > 0 ? [...this.getErrorElementId, idInput] : [idInput];
      this.setErrorElementId(tempList);

      if (
        idInput?.includes('imageReferenceThumbnail_') &&
        this.styleFor !== undefined &&
        this.styleFor === REFERENCE_STYLE_FOR.REPORT_REFERENCE.toString()
      ) {
        const element = document.getElementById('wrap-body-reference-element');

        if (element) {
          element.style.width = '100%';
        }
      }

      hideElementById(idInput);
    }
  }

  handleClickToTitle(document: any) {
    if (this.styleFor !== undefined && this.styleFor === REFERENCE_STYLE_FOR.REPORT_REFERENCE.toString()) {
      const originUrl = document?.data?.source_uri || this.dataModelForView?.originUrl || '';

      if (originUrl?.trim()?.length > 0) {
        this.openOriginal(originUrl);
      }
    } else {
      this.showDetail(document);
    }
  }
}
